import React, { useState, useEffect, useRef } from 'react';
import { Eye, Edit, Download, Plus, X, Search, Paperclip, Send, FileText, UserPlus } from 'lucide-react';
import JSZip from 'jszip';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

export default function KardexEstAdmision({ setCurrentPage, readOnly = false, hideHeader = false }) {
  const [showModal, setShowModal] = useState(false);
  const [showDocModal, setShowDocModal] = useState(false);
  const [showDetallesModal, setShowDetallesModal] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [estudiantes, setEstudiantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('preadmision');
  const currentYear = new Date().getFullYear();
  const [selectedGestion, setSelectedGestion] = useState(currentYear);
  const [editingId, setEditingId] = useState(null);
  const [selectedEstudiante, setSelectedEstudiante] = useState(null);
  const [comentarioEdit, setComentarioEdit] = useState({});
  const [mostrarHabilitados, setMostrarHabilitados] = useState(false);
  const fileInputRefs = useRef({});

  const initialFormData = {
    modalidadPostulacion: '',
    nombres: '', apellidoPaterno: '', apellidoMaterno: '',
    cedulaIdentidad: '', extension: '', complemento: '',
    fechaNacimiento: '', edad: '', lugarNacimiento: '', lugarNacimientoOtro: '',
    direccion: '', numeroTelefono: '', correoElectronico: '',
    // Familiar cercano
    familiarNombre: '', familiarParentesco: '', familiarTelefono: '',
    // Contacto emergencia
    emergenciaNombre: '', emergenciaParentesco: '', emergenciaTelefono: '',
    // Datos de salud
    tipoSangre: '', alergias: '', enfermedadBase: '', lesiones: '',
    // Datos de usuario
    username: '', password: ''
  };

  const [formData, setFormData] = useState(initialFormData);

  const [documents, setDocuments] = useState([
    { id: 1, name: 'Fotografía Título Bachiller o Comprobante Gestión Título Bachiller o Libreta 6to Sec', file: null, estado: null },
    { id: 2, name: 'Fotografía Cédula de Identidad', file: null, estado: null },
    { id: 3, name: 'Fotografía Comprobante Bancario', file: null, estado: null },
    { id: 4, name: 'Fotografía del Postulante', file: null, estado: null }
  ]);

  // Estado para validación de periodo
  const [periodoCerrado, setPeriodoCerrado] = useState(false);
  const [mensajePeriodo, setMensajePeriodo] = useState('');

  // Verificar periodo al cargar y al cambiar filtro
  useEffect(() => {
    const checkPeriodo = async () => {
      const accion = tipoFiltro === 'preadmision' ? 'inscripcion_cpa' :
        tipoFiltro === 'admision' ? 'inscripcion_paa' : 'inscripcion_matriculacion';
      try {
        const res = await fetch(`${API_URL}/auth/verificar-periodo/${accion}`);
        const data = await res.json();
        setPeriodoCerrado(!data.permitido);
        setMensajePeriodo(data.mensaje || 'La acción no se encuentra habilitada en la fecha actual. Por favor, verifique el periodo establecido por Dirección Académica.');
      } catch (err) {
        console.error('Error verificando periodo:', err);
      }
    };
    checkPeriodo();
    loadEstudiantes();
  }, [tipoFiltro, selectedGestion]);

  useEffect(() => {
    if (showModal) {
      fetch(`${API_URL}/auth/payment-config/preadmision`)
        .then(res => res.json())
        .then(data => setPaymentConfig(data))
        .catch(err => console.error('Error:', err));
    }
  }, [showModal]);

  // Calcular edad automáticamente
  useEffect(() => {
    if (formData.fechaNacimiento) {
      const birthDate = new Date(formData.fechaNacimiento);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
      setFormData(prev => ({ ...prev, edad: age.toString() }));
    }
  }, [formData.fechaNacimiento]);

  const loadEstudiantes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/kardex/estudiantes?tipo=${tipoFiltro}&gestion=${selectedGestion}`);
      const data = await response.json();
      setEstudiantes(data.estudiantes || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (docId, e) => {
    const file = e.target.files[0];
    if (file) {
      setDocuments(prev => prev.map(doc =>
        doc.id === docId ? { ...doc, file, estado: 'pendiente' } : doc
      ));
    }
  };

  const calcularPorcentajeDoc = (est) => {
    const totalDocs = 4;
    const docsSubidos = (est.doc_titulo ? 1 : 0) + (est.doc_cedula ? 1 : 0) + (est.doc_comprobante ? 1 : 0) + (est.doc_foto ? 1 : 0);
    return Math.round((docsSubidos / totalDocs) * 100);
  };

  const handleSubmit = async () => {
    try {
      const isEditing = !!editingId;
      const url = isEditing
        ? `${API_URL}/kardex/estudiantes/${editingId}`
        : `${API_URL}/kardex/estudiantes-with-files`;
      const method = isEditing ? 'PUT' : 'POST';

      const data = new FormData();

      // Añadir datos del formulario
      data.append('tipo', tipoFiltro);
      data.append('modalidad_postulacion', formData.modalidadPostulacion);
      data.append('nombres', formData.nombres);
      data.append('apellido_paterno', formData.apellidoPaterno);
      data.append('apellido_materno', formData.apellidoMaterno);
      data.append('cedula_identidad', formData.cedulaIdentidad);
      data.append('extension', formData.extension);
      data.append('complemento', formData.complemento);
      data.append('fecha_nacimiento', formData.fechaNacimiento || '');
      data.append('lugar_nacimiento', formData.lugarNacimiento);
      data.append('direccion', formData.direccion);
      data.append('telefono', formData.numeroTelefono);
      data.append('email', formData.correoElectronico);
      data.append('familiar_nombre', formData.familiarNombre);
      data.append('familiar_parentesco', formData.familiarParentesco);
      data.append('familiar_telefono', formData.familiarTelefono);
      data.append('emergencia_nombre', formData.emergenciaNombre);
      data.append('emergencia_parentesco', formData.emergenciaParentesco);
      data.append('emergencia_telefono', formData.emergenciaTelefono);
      data.append('tipo_sangre', formData.tipoSangre);
      data.append('alergias', formData.alergias);
      data.append('enfermedad_base', formData.enfermedadBase);
      data.append('lesiones', formData.lesiones);

      if (!isEditing) {
        data.append('username', formData.username || formData.cedulaIdentidad);
        data.append('password', formData.password || formData.cedulaIdentidad);
      }

      // Añadir archivos
      documents.forEach(doc => {
        if (doc.file) {
          // Mapear IDs de documentos a nombres de campos esperados por el backend
          const fieldMap = {
            1: 'doc_titulo',
            2: 'doc_cedula',
            3: 'doc_comprobante',
            4: 'doc_foto'
          };
          const fieldName = fieldMap[doc.id];
          if (fieldName) {
            data.append(fieldName, doc.file);
          }
        }
      });

      const response = await fetch(url, {
        method,
        body: data // Al enviar FormData, no se pone Content-Type (el navegador lo hace)
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Guardado exitosamente');
        resetForm();
        loadEstudiantes();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al guardar');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error al guardar');
    }
  };

  const resetForm = () => {
    setShowModal(false);
    setShowDocModal(false);
    setShowDetallesModal(false);
    setEditingId(null);
    setFormData(initialFormData);
    setDocuments(documents.map(d => ({ ...d, file: null, estado: null })));
  };

  const handleEdit = (est) => {
    setEditingId(est.id_estudiante);
    setFormData({
      modalidadPostulacion: est.modalidad_postulacion || '',
      nombres: est.nombres || '',
      apellidoPaterno: est.apellido_paterno || '',
      apellidoMaterno: est.apellido_materno || '',
      cedulaIdentidad: est.cedula_identidad || '',
      extension: est.extension || '',
      complemento: est.complemento || '',
      fechaNacimiento: est.fecha_nacimiento ? est.fecha_nacimiento.split('T')[0] : '',
      edad: '',
      lugarNacimiento: est.lugar_nacimiento || '',
      direccion: est.direccion || '',
      numeroTelefono: est.telefono || '',
      correoElectronico: est.email || '',
      familiarNombre: est.familiar_nombre || '',
      familiarParentesco: est.familiar_parentesco || '',
      familiarTelefono: est.familiar_telefono || '',
      emergenciaNombre: est.emergencia_nombre || '',
      emergenciaParentesco: est.emergencia_parentesco || '',
      emergenciaTelefono: est.emergencia_telefono || '',
      tipoSangre: est.tipo_sangre || '',
      alergias: est.alergias || '',
      enfermedadBase: est.enfermedad_base || '',
      lesiones: est.lesiones || ''
    });

    setDocuments([
      { id: 1, name: 'Fotografía Título Bachiller o Comprobante Gestión Título Bachiller o Libreta 6to Sec', file: null, estado: est.doc_titulo ? 'aprobado' : null },
      { id: 2, name: 'Fotografía Cédula de Identidad', file: null, estado: est.doc_cedula ? 'aprobado' : null },
      { id: 3, name: 'Fotografía Comprobante Bancario', file: null, estado: est.doc_comprobante ? 'aprobado' : null },
      { id: 4, name: 'Fotografía del Postulante', file: null, estado: est.doc_foto ? 'aprobado' : null }
    ]);

    setShowModal(true);
  };

  const handleHabilitarAcceso = async (est) => {
    if (!window.confirm(`¿Está seguro de habilitar el acceso para ${est.nombres} ${est.apellido_paterno}?`)) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/kardex/habilitar-estudiante`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_inscripcion: est.id_inscripcion })
      });
      if (response.ok) {
        alert('Acceso habilitado correctamente');
        loadEstudiantes();
      } else {
        alert('Error al habilitar acceso');
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCambiarEstadoEspecial = async (est, nuevoEstado) => {
    if (!window.confirm(`¿Está seguro de cambiar el estado a ${nuevoEstado} para ${est.nombres}? ${nuevoEstado === 'Habilitado' ? 'Esto lo matriculará en Licenciatura y cambiará su código.' : ''}`)) {
      return;
    }
    try {
      const response = await fetch(`${API_URL}/kardex/estudiantes-especial/${est.id_estudiante}/estado`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ estado: nuevoEstado })
      });
      if (response.ok) {
        let msg = 'Estado actualizado';
        if (nuevoEstado === 'Habilitado') msg = 'Estudiante habilitado y matriculado';
        if (nuevoEstado === 'Deshabilitado') msg = 'Estudiante deshabilitado';
        if (nuevoEstado === 'Pendiente') msg = 'Estudiante puesto en pendiente';
        alert(msg);
        loadEstudiantes();
      } else {
        const data = await response.json();
        alert(data.message || 'Error al cambiar estado');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error de conexión');
    }
  };

  const handleCambiarVerificacion = async (est, nuevoEstado) => {
    // 1. Capturamos el ID desde las opciones disponibles
    const idFila = est.id_inscripcion || est.id || est.id_estudiante;

    // Si no hay un ID válido, cancelamos silenciosamente para evitar peticiones erróneas
    if (!idFila) {
      console.error("Error: No se encontró un ID válido en el objeto:", est);
      return;
    }

    try {
      // 2. Enviamos la petición directo al servidor
      const response = await fetch(`${API_URL}/kardex/estudiantes-especial/${idFila}/verificacion`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verificacion: nuevoEstado })
      });

      if (response.ok) {
        // 3. Si todo sale bien, actualizamos el estado de React inmediatamente
        setEstudiantes(prev => prev.map(e =>
          (e.id_inscripcion === idFila || e.id === idFila || e.id_estudiante === idFila)
            ? { ...e, verificacion: nuevoEstado }
            : e
        ));
      } else {
        // Si hay un error en el servidor, solo lo registramos en la consola de desarrollo
        const textoError = await response.text();
        console.error("El servidor rechazó la petición:", textoError);
      }
    } catch (error) {
      // Si hay un fallo de red o conexión, lo registramos en la consola
      console.error('Error de conexión con el servidor:', error);
    }
  };

  const handleComentarioChange = (id, value) => {
    setComentarioEdit(prev => ({ ...prev, [id]: value }));
  };

  const enviarComentario = async (est) => {
    const comentario = comentarioEdit[est.id_estudiante];
    if (!comentario) return;
    try {
      await fetch(`${API_URL}/kardex/estudiantes/${est.id_estudiante}/comentario`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comentario, enviar_email: true })
      });
      alert('Comentario guardado y enviado por correo');
      loadEstudiantes();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const descargarTodosAdjuntos = async (est) => {
    try {
      const studentId = est.id_estudiante || est.codigo || est.cedula_identidad || est.ci;
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/kardex/estudiantes/${studentId}/documentos-zip`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const safeCode = (est.codigo_estudiante || est.codigo || est.ci || est.cedula_identidad || 'postulante').replace(/\s+/g, '_');
      const safeName = `${est.apellido_paterno || ''}_${est.apellido_materno || ''}_${est.nombres || ''}`.trim().replace(/\s+/g, '_');
      link.download = `Documentos_Admision_${safeCode}_${safeName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error al descargar ZIP:', error);
      alert('Ocurrió un error al descargar el archivo .zip de los documentos de admisión.');
    }
  };

  const abrirModalDocumentos = (est) => {
    // Primero cargamos los datos en el formulario general para evitar nulos al guardar
    setEditingId(est.id_estudiante);
    setFormData({
      modalidadPostulacion: est.modalidad_postulacion || '',
      nombres: est.nombres || '',
      apellidoPaterno: est.apellido_paterno || '',
      apellidoMaterno: est.apellido_materno || '',
      cedulaIdentidad: est.cedula_identidad || '',
      extension: est.extension || '',
      complemento: est.complemento || '',
      fechaNacimiento: est.fecha_nacimiento ? est.fecha_nacimiento.split('T')[0] : '',
      edad: '',
      lugarNacimiento: est.lugar_nacimiento || '',
      direccion: est.direccion || '',
      numeroTelefono: est.telefono || '',
      correoElectronico: est.email || '',
      familiarNombre: est.familiar_nombre || '',
      familiarParentesco: est.familiar_parentesco || '',
      familiarTelefono: est.familiar_telefono || '',
      emergenciaNombre: est.emergencia_nombre || '',
      emergenciaParentesco: est.emergencia_parentesco || '',
      emergenciaTelefono: est.emergencia_telefono || '',
      tipoSangre: est.tipo_sangre || '',
      alergias: est.alergias || '',
      enfermedadBase: est.enfermedad_base || '',
      lesiones: est.lesiones || ''
    });

    setSelectedEstudiante(est);
    setDocuments([
      { id: 1, name: 'Fotografía Título Bachiller o Comprobante Gestión Título Bachiller o Libreta 6to Sec', file: null, estado: est.doc_titulo ? 'aprobado' : null },
      { id: 2, name: 'Fotografía Cédula de Identidad', file: null, estado: est.doc_cedula ? 'aprobado' : null },
      { id: 3, name: 'Fotografía Comprobante Bancario', file: null, estado: est.doc_comprobante ? 'aprobado' : null },
      { id: 4, name: 'Fotografía del Postulante', file: null, estado: est.doc_foto ? 'aprobado' : null }
    ]);
    setShowDocModal(true);
  };

  const abrirDetalles = (est) => {
    setSelectedEstudiante(est);
    setShowDetallesModal(true);
  };

  // Descargar formulario CPA o PAA según el tipo de filtro
  const descargarFormularioCPAPAA = (est) => {
    console.log('Datos del estudiante:', est); // Debug

    const esCPA = tipoFiltro === 'preadmision';
    const titulo = esCPA ? 'FORMULARIO CURSO DE PRE-ADMISIÓN (C.P.A)' : 'FORMULARIO DE PRUEBA DE ADMISIÓN (P.A.A)';

    const fechaNac = est.fecha_nacimiento ? new Date(est.fecha_nacimiento).toLocaleDateString('es-BO') : '';

    const html = `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>${titulo}</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; margin: 30px; }
        h1, h2 { text-align: center; margin: 5px 0; }
        h1 { font-size: 16px; }
        h2 { font-size: 13px; text-align: left; margin-top: 20px; }
        table { width: 100%; border-collapse: collapse; margin-top: 5px; }
        td, th { border: 1px solid #000; padding: 6px; vertical-align: middle; }
        .no-border { border: none; }
        .center { text-align: center; }
        .right { text-align: right; }
        .blue { background: #9db7e8; font-weight: bold; }
        .checkbox { width: 18px; height: 18px; border: 1px solid #000; display: inline-block; }
        .page-break { page-break-after: always; }
        .signature { margin-top: 50px; text-align: center; }
        .seal-box { width: 180px; height: 60px; border: 2px dashed #000; text-align: center; font-size: 14px; font-weight: bold; position: absolute; top: 120px; left: 30px; line-height: 60px; }
        .register-number { text-align: right; font-weight: bold; font-size: 16px; margin-top: 30px; }
        .footer { text-align: center; margin-top: 30px; font-size: 12px; }
        @page { size: A4; margin: 25mm; }
        @media print { .no-print { display: none; } }
    </style>
</head>
<body>
    <div class="no-print" style="margin-bottom:20px;padding:15px;background:#f0f0f0;border-radius:8px;">
        <button onclick="window.print()" style="padding:10px 20px;cursor:pointer;background:#1e3a8a;color:white;border:none;border-radius:5px;font-size:14px;">📄 Imprimir / Guardar como PDF</button>
        <span style="margin-left:15px;color:#666;">Tip: Al imprimir, selecciona "Guardar como PDF" en el destino</span>
    </div>
    
    <div class="seal-box"><div class="seal-text">SELLO DE KARDEX</div></div>
    <h1>${titulo}</h1>
    <div class="register-number">NÚMERO DE REGISTRO: ${est.codigo || '_______________'}</div>

    <h2>1. DATOS PERSONALES</h2>
    <table>
        <tr><td>NOMBRE(S)</td><td>PRIMER APELLIDO</td><td>SEGUNDO APELLIDO</td></tr>
        <tr><td style="height:25px;">${est.nombres || ''}</td><td>${est.apellido_paterno || ''}</td><td>${est.apellido_materno || ''}</td></tr>
        <tr><td>C.I.</td><td>EXP.</td><td>EDAD</td></tr>
        <tr><td style="height:25px;">${est.cedula_identidad || ''}</td><td>${est.extension || ''}</td><td>${est.edad || ''}</td></tr>
        <tr><td>FECHA DE NACIMIENTO<br>dd/mm/aa</td><td colspan="2">LUGAR DE NACIMIENTO</td></tr>
        <tr><td style="height:25px;">${fechaNac}</td><td colspan="2">${est.lugar_nacimiento || ''}</td></tr>
        <tr><td colspan="3">DIRECCIÓN / DOMICILIO ACTUAL</td></tr>
        <tr><td colspan="3" style="height:25px;">${est.direccion || ''}</td></tr>
        <tr><td>NÚMERO TELÉFONO / CELULAR</td><td colspan="2">E-MAIL</td></tr>
        <tr><td style="height:25px;">${est.telefono || ''}</td><td colspan="2">${est.email || ''}</td></tr>
    </table>

    <h2>2. REQUISITOS DE HABILITACIÓN (LLENADO POR KARDEX)</h2>
    <table>
        <tr class="blue center"><td>DOCUMENTO</td><td>CUENTA</td><td>NO CUENTA</td><td>NO CORRESPONDE</td><td>OBSERVACIONES</td></tr>
        <tr><td class="blue">FOTOCOPIA SIMPLE DE TITULO DE BACHILLER</td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td></td></tr>
        <tr><td class="blue">FOTOCOPIA C.I.</td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td></td></tr>
        <tr><td class="blue">COMPROBANTE DEPOSITO BANCARIO</td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td class="center"><div class="checkbox"></div></td><td></td></tr>
    </table>

    <div class="page-break"></div>

    <h2>3. DECLARACIÓN JURADA</h2>
    <table>
        <tr><td style="height:150px; text-align: justify;">
            Yo ${est.nombres || ''} ${est.apellido_paterno || ''} ${est.apellido_materno || ''} , hábil por derecho, con Cédula de Identidad N° ${est.cedula_identidad || ''} ${est.extension || ''}, de manera voluntaria acepto los términos establecidos en la Convocatoria y sus procesos de admisión y registro, así mismo declaro en honor a la verdad, que cumplo a cabalidad con los requisitos indispensables exigidos en la convocatoria, siendo que todos los datos proporcionados en el presente formulario son verídicos, por lo que autorizo que la información proporcionada sea verificada y contrastada por la EBID, comprometiéndome a la presentación de la documentación original de acuerdo al cronograma establecido. Asímismo, asumo plena responsabilidad por cualquier situación relacionada con mi salud o accidentes que pudieran ocurrir durante el desarrollo de mis actividades académicas y extracurriculares en la EBID. En este sentido, autorizo/no autorizo a la EBID que, si fuera necesario, contacte a un familiar o contacto de emergencia cuando sea necesario. En caso de no estar de acuerdo con lo estipulado, exonero a la institución de toda responsabilidad respecto de accidentes relacionados a mi persona y de salud dentro de la Institución.
        </td></tr>
    </table>

    <div class="signature">
        ..............................................................<br>
        <strong>Firma Estudiante</strong>
    </div>

    <div class="footer">
        El Alto, _______ / _______ / _______ <br>
        <strong>Firma Postulante</strong>
    </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      alert('El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.');
    }
  };

  // Descargar Certificado de Admisión
  const descargarCertificadoAdmision = (est) => {
    const fechaHoy = new Date().toLocaleDateString('es-BO', { day: '2-digit', month: 'long', year: 'numeric' });
    const gestionActual = new Date().getFullYear();

    const html = `
<!DOCTYPE html>
<html lang="es">
<head>
    <meta charset="UTF-8">
    <title>Certificado de Admisión</title>
    <style>
        body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 40px; }
        .header { text-align: center; margin-bottom: 20px; }
        .logo { height: 60px; margin-bottom: 10px; }
        .title { font-size: 18px; font-weight: bold; margin: 20px 0; text-transform: uppercase; text-align: center; }
        .content { text-align: justify; line-height: 1.8; margin: 20px 30px; font-size: 13px; }
        .signature { margin-top: 100px; text-align: center; }
        .firma-line { border-top: 1px solid #000; width: 250px; margin: 0 auto; padding-top: 5px; }
        .footer { position: fixed; bottom: 30px; width: 100%; text-align: center; font-size: 10px; color: #666; }
        @page { size: A4; margin: 25mm; }
        @media print { .no-print { display: none; } }
    </style>
</head>
<body>
    <button class="no-print" onclick="window.print()" style="margin-bottom:20px;padding:10px 20px;cursor:pointer;">Imprimir / Guardar PDF</button>
    
    <div class="header">
        <h2>ESCUELA BOLIVIANA INTERCULTURAL DE DANZA</h2>
        <h3>DIRECCIÓN ACADÉMICA</h3>
    </div>
    
    <div class="title">CERTIFICADO DE ADMISIÓN</div>
    
    <div class="content">
        <p>La Dirección Académica de la Escuela Boliviana Intercultural de Danza, certifica que:</p>
        
        <p style="text-align:center;font-weight:bold;font-size:16px;margin:30px 0;">
            ${est.nombres || ''} ${est.apellido_paterno || ''} ${est.apellido_materno || ''}
        </p>
        
        <p>Con Cédula de Identidad N° <strong>${est.cedula_identidad || '________'} ${est.extension || ''}</strong>, 
        ha sido <strong>ADMITIDO(A)</strong> como estudiante regular de esta institución en la Carrera de 
        <strong>${est.carrera_nombre || '________________________'}</strong>${est.mencion_nombre ? `, Mención: ${est.mencion_nombre}` : ''},
        para la gestión académica ${gestionActual}.</p>
        
        <p>Es cuanto se certifica para los fines consiguientes del interesado(a).</p>
    </div>
    
    <p style="text-align:right;margin-top:60px;margin-right:30px;">El Alto, ${fechaHoy}</p>
    
    <div class="signature">
        <div class="firma-line">
            <strong>Director(a) Académico(a)</strong><br>
            EBID
        </div>
    </div>

    <div class="footer">
        Av. Costanera Nro. 100, Zona Villa Tunari - El Alto, Bolivia <br>
        Teléfono: 2-2860000 | Email: info@ebid.edu.bo
    </div>
</body>
</html>`;

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    } else {
      alert('El navegador bloqueó la ventana emergente. Por favor, permite las ventanas emergentes para este sitio.');
    }
  };

  const filtered = estudiantes.filter(e => {
    const search = searchTerm.toLowerCase();
    const matchesSearch = searchTerm === '' ||
      `${e.nombres} ${e.apellido_paterno} ${e.cedula_identidad} ${e.codigo}`.toLowerCase().includes(search);

    // Lógica de habilitación (debe coincidir con la del renderizado de la tabla)
    const reqMaterias = tipoFiltro === 'preadmision' ? 1 : 1;
    const estaHabilitado = ((e.max_nota || e.promedio_general || 0) >= 61) && (e.materias_con_nota || 0) >= reqMaterias;

    // Si no se pide mostrar habilitados y el estudiante lo está, lo ocultamos
    if (!mostrarHabilitados && estaHabilitado) return false;

    return matchesSearch;
  });

  const extensiones = ['LP', 'CB', 'SC', 'OR', 'PT', 'TJ', 'CH', 'BN', 'PD'];
  const departamentos = ['La Paz', 'Cochabamba', 'Santa Cruz', 'Oruro', 'Potosí', 'Tarija', 'Chuquisaca', 'Beni', 'Pando'];

  return (
    <div className="p-4 md:p-6 overflow-hidden">
      {/* Header */}

      {!hideHeader && !readOnly && (
        <div className="mb-4 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-800">Kardex &gt; Estudiantes &gt; Admision</h1>
          <span className="text-lg font-semibold text-gray-700"></span>
        </div>
      )}

      <div className="mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-wrap gap-4 items-end ">
          {!readOnly && (
            <button
              onClick={() => {
                if (periodoCerrado) {
                  alert(mensajePeriodo);
                  return;
                }
                setCurrentPage && setCurrentPage('registro-preadmision', { source: 'kardex' });
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-full ${periodoCerrado ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'} text-white`}
            >
              <Plus size={20} />
              <span className="font-medium">Añadir estudiante</span>
            </button>
          )}
          {/* Gestión */}
          <div className="relative">
            <select
              value={tipoFiltro}
              onChange={(e) => setTipoFiltro(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
            >
              <option value="preadmision">Curso de Preadmisión</option>
              <option value="admision">Prueba de Admisión Académica</option>
              <option value="especial">Admisión Especial</option>
            </select>
            <span className="absolute right-3 top-[12px] pointer-events-none text-gray-400 text-xs">▼</span>
          </div>
          <div className="relative">
            <select
              value={selectedGestion}
              onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 rounded-full bg-white text-sm appearance-none pr-8 hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 min-w-[100px]"
            >
              {[2024, 2025, 2026, 2027].map(g => (
                <option key={g} value={g}>Gestión {g}</option>
              ))}
            </select>
            <span className="absolute right-3 top-[12px] pointer-events-none text-gray-400 text-xs">▼</span>
          </div>
          {/* Buscar y Descargar */}
          <div className="flex-grow flex items-end gap-2">
            <div className="relative flex-grow">
              <div className="relative">
                <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Buscar por nombre, apellido o CI..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 hover:border-blue-500"
                />
              </div>
            </div>

          </div>
          <button
            onClick={() => setMostrarHabilitados(!mostrarHabilitados)}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${mostrarHabilitados
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
          >
            {mostrarHabilitados ? 'OCULTAR APROBADOS' : 'VER TODOS (INC. APROBADOS)'}
          </button>
          <button
            onClick={() => {
              const headers = [
                'Código', 'Nombres', 'Apellido Paterno', 'Apellido Materno', 'Cédula', 'Extensión', 'Complemento',
                'Fecha Nacimiento', 'Edad', 'Lugar Nacimiento', 'Dirección',
                'Familiar Nombre', 'Familiar Parentesco', 'Familiar Teléfono',
                'Emergencia Nombre', 'Emergencia Parentesco', 'Emergencia Teléfono',
                'Tipo Sangre', 'Alergias', 'Enfermedad Base', 'Lesiones',
                '% Documentación', 'Estado'
              ];
              const rows = filtered.map((e, i) => [
                e.codigo || '-',
                e.nombres || '',
                e.apellido_paterno || '',
                e.apellido_materno || '',
                e.cedula_identidad || '',
                e.extension || '',
                e.complemento || '',
                e.fecha_nacimiento ? new Date(e.fecha_nacimiento).toLocaleDateString() : '',
                e.edad || '',
                e.lugar_nacimiento || '',
                e.direccion || '',
                e.familiar_nombre || '',
                e.familiar_parentesco || '',
                e.familiar_telefono || '',
                e.emergencia_nombre || '',
                e.emergencia_parentesco || '',
                e.emergencia_telefono || '',
                e.tipo_sangre || '',
                e.alergias || '',
                e.enfermedad_base || '',
                e.lesiones || '',
                calcularPorcentajeDoc(e) + '%',
                (tipoFiltro === 'especial')
                  ? (e.estado || 'Pendiente')
                  : (e.materias_con_nota === undefined || e.materias_con_nota === 0 || e.promedio_general === null)
                    ? 'Pendiente'
                    : ((e.max_nota >= 61 || e.promedio_general >= 61) && e.materias_con_nota >= (tipoFiltro === 'preadmision' ? 1 : 1))
                      ? 'Habilitado'
                      : 'Deshabilitado'
              ]);
              const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
              const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
              const link = document.createElement('a');
              link.href = URL.createObjectURL(blob);
              link.download = `estudiantes_admision_${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(link.href);
            }}
            className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700" title="Descargar Excel"
          >
            <Download size={20} />
          </button>
        </div>
      </div>

      {/* Toolbar */}
      {/* <div className="mb-4 flex flex-wrap gap-3 items-center">
        {!readOnly && (
          <button
            onClick={() => {
              if (periodoCerrado) {
                alert(mensajePeriodo);
                return;
              }
              setCurrentPage && setCurrentPage('registro-preadmision', { source: 'kardex' });
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-full ${periodoCerrado ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-900 hover:bg-blue-800'} text-white`}
          >
            <Plus size={20} />
            <span className="font-medium">Añadir estudiante</span>
          </button>
        )}

        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-full"
        >
          <option value="preadmision">Curso de Preadmisión</option>
          <option value="admision">Prueba de Admisión Académica</option>
          <option value="especial">Admisión Especial</option>
        </select>

        <select
          value={selectedGestion}
          onChange={(e) => setSelectedGestion(parseInt(e.target.value))}
          className="px-4 py-2 border border-gray-300 rounded-full"
        >
          {[2024, 2025, 2026, 2027].map(g => (
            <option key={g} value={g}>Gestión {g}</option>
          ))}
        </select>

        <div className="flex-1 flex gap-2">
          <div className="relative flex-1 group">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre, apellido o CI..."
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-full focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setMostrarHabilitados(!mostrarHabilitados)}
            className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${mostrarHabilitados
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
          >
            {mostrarHabilitados ? 'OCULTAR APROBADOS' : 'VER TODOS (INC. APROBADOS)'}
          </button>
        </div>

        <button
          onClick={() => {
            const headers = [
              'Código', 'Nombres', 'Apellido Paterno', 'Apellido Materno', 'Cédula', 'Extensión', 'Complemento',
              'Fecha Nacimiento', 'Edad', 'Lugar Nacimiento', 'Dirección',
              'Familiar Nombre', 'Familiar Parentesco', 'Familiar Teléfono',
              'Emergencia Nombre', 'Emergencia Parentesco', 'Emergencia Teléfono',
              'Tipo Sangre', 'Alergias', 'Enfermedad Base', 'Lesiones',
              '% Documentación', 'Estado'
            ];
            const rows = filtered.map((e, i) => [
              e.codigo || '-',
              e.nombres || '',
              e.apellido_paterno || '',
              e.apellido_materno || '',
              e.cedula_identidad || '',
              e.extension || '',
              e.complemento || '',
              e.fecha_nacimiento ? new Date(e.fecha_nacimiento).toLocaleDateString() : '',
              e.edad || '',
              e.lugar_nacimiento || '',
              e.direccion || '',
              e.familiar_nombre || '',
              e.familiar_parentesco || '',
              e.familiar_telefono || '',
              e.emergencia_nombre || '',
              e.emergencia_parentesco || '',
              e.emergencia_telefono || '',
              e.tipo_sangre || '',
              e.alergias || '',
              e.enfermedad_base || '',
              e.lesiones || '',
              calcularPorcentajeDoc(e) + '%',
              (tipoFiltro === 'especial')
                ? (e.estado || 'Pendiente')
                : (e.materias_con_nota === undefined || e.materias_con_nota === 0 || e.promedio_general === null)
                  ? 'Pendiente'
                  : ((e.max_nota >= 61 || e.promedio_general >= 61) && e.materias_con_nota >= (tipoFiltro === 'preadmision' ? 1 : 1))
                    ? 'Habilitado'
                    : 'Deshabilitado'
            ]);
            const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n');
            const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = `estudiantes_admision_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
          }}
          className="p-2 bg-green-600 text-white rounded-full hover:bg-green-700" title="Descargar Excel"
        >
          <Download size={20} />
        </button>
      </div> */}

      {/* Tabla simplificada */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto max-w-full" style={{ maxWidth: 'calc(100vw - 280px)' }}>
          <table className="w-full text-xs whitespace-nowrap">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-2 py-3 text-center font-semibold border">Código</th>
                <th className="px-2 py-3 text-center font-semibold border">Nombres</th>
                <th className="px-2 py-3 text-center font-semibold border">Apellidos</th>
                <th className="px-2 py-3 text-center font-semibold border">Cédula de Identidad</th>
                <th className="px-2 py-3 text-center font-semibold border">Extensión</th>
                <th className="px-2 py-3 text-center font-semibold border">Fecha de Nacimiento</th>
                <th className="px-2 py-3 text-center font-semibold border">Lugar de Nacimiento</th>
                <th className="px-2 py-3 text-center font-semibold border">% Documentación</th>
                <th className="px-2 py-3 text-center font-semibold border">Estado Verificación</th>
                <th className="px-2 py-3 text-center font-semibold border">Estado Aprovación</th>
                {!readOnly && (
                  <>
                    <th className="px-2 py-3 text-center font-semibold border">Acciones</th>
                    <th className="px-2 py-3 text-center font-semibold border">Comentarios</th>
                  </>
                )}
                {readOnly && (
                  <th className="px-2 py-3 text-center font-semibold border">Ver Detalles</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="11" className="px-4 py-8 text-center text-gray-500 border">Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="12" className="px-4 py-8 text-center text-gray-500 border">No hay estudiantes registrados</td></tr>
              ) : filtered.map((est, index) => {
                // const tieneNotas = est.materias_con_nota > 0 && est.promedio_general !== null;
                // const reqMaterias = tipoFiltro === 'preadmision' ? 1 : 1;
                // const estaHabilitado = tieneNotas && (est.max_nota >= 61 || est.promedio_general >= 61) && est.materias_con_nota >= reqMaterias;

                // 1. Identificar si estamos en la modalidad PAA (admision)
                const esPAA = tipoFiltro === 'admision';

                // 2. Definir el requerimiento de materias según la modalidad (PAA no exige materias)
                const reqMaterias = esPAA ? 0 : 1;

                // 3. Evaluar la existencia de notas sin exigir materias cursadas para PAA
                const tieneNotas = esPAA
                  ? (est.max_nota !== null || est.promedio_general !== null)
                  : (parseInt(est.materias_con_nota || 0) > 0 && est.promedio_general !== null);

                // 4. Asegurar el parseo de notas a valores numéricos flotantes reales
                const notaExamen = parseFloat(est.max_nota || 0);
                const notaPromedio = parseFloat(est.promedio_general || 0);

                // 5. Determinar la habilitación real
                const estaHabilitado = tieneNotas &&
                  (notaExamen >= 61 || notaPromedio >= 61) &&
                  parseInt(est.materias_con_nota || 0) >= reqMaterias;

                return (
                  <tr key={est.id_estudiante} className="hover:bg-gray-50">
                    <td className="px-2 py-2 text-center border">{est.codigo || '-'}</td>
                    <td className="px-2 py-2 text-center border">{est.nombres || '-'}</td>
                    <td className="px-2 py-2 text-center border">{`${est.apellido_paterno || ''} ${est.apellido_materno || ''}`.trim() || '-'}</td>
                    <td className="px-2 py-2 text-center border">{est.cedula_identidad || '-'}</td>
                    <td className="px-2 py-2 text-center border">{est.extension || '-'}</td>
                    <td className="px-2 py-2 text-center border">{est.fecha_nacimiento ? new Date(est.fecha_nacimiento).toLocaleDateString() : '-'}</td>
                    <td className="px-2 py-2 text-center border">{est.lugar_nacimiento || '-'}</td>
                    <td className="px-2 py-2 text-center border font-medium">{calcularPorcentajeDoc(est)}%</td>
                    <td className="px-2 py-2 text-center border">
                      {readOnly ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${est.verificacion === 'Verificado' ? 'bg-teal-100 text-teal-800' : 'bg-amber-100 text-amber-800'}`}>
                          {est.verificacion || 'No verificado'}
                        </span>
                      ) : (
                        <select
                          value={est.verificacion || 'No verificado'}
                          onChange={(e) => handleCambiarVerificacion(est, e.target.value)}
                          className={`px-2 py-1 rounded-full text-xs font-semibold border border-gray-300 cursor-pointer outline-none ${est.verificacion === 'Verificado'
                            ? 'bg-green-100 text-green-800 border-teal-200'
                            : 'bg-yellow-100 text-yellow-800 border-amber-200'
                            }`}
                        >
                          <option value="No verificado">No verificado</option>
                          <option value="Verificado">Verificado</option>
                        </select>
                      )}
                    </td>
                    <td className="px-2 py-2 text-center border">
                      {tipoFiltro === 'especial' ? (
                        <select
                          value={est.estado || 'Pendiente'}
                          onChange={(e) => handleCambiarEstadoEspecial(est, e.target.value)}
                          className={`px-2 py-1 rounded-full text-xs font-semibold border-none cursor-pointer ${est.estado === 'Habilitado' ? 'bg-green-100 text-green-800' :
                            est.estado === 'Deshabilitado' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}
                        >
                          <option value="Pendiente">Pendiente</option>
                          <option value="Habilitado">Habilitado</option>
                          <option value="Deshabilitado">Deshabilitado</option>
                        </select>
                      ) : !tieneNotas ? (
                        <span className="px-2 py-1 rounded-full text-xs whitespace-nowrap bg-yellow-100 text-yellow-800">Pendiente</span>
                      ) : (
                        <span className={`px-2 py-1 rounded-full text-xs whitespace-nowrap ${estaHabilitado ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                          {estaHabilitado ? 'Habilitado' : 'Deshabilitado'}
                        </span>
                      )}
                    </td>

                    {!readOnly ? (
                      <>
                        <td className="px-2 py-2 text-center border">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => abrirDetalles(est)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Ver detalles completos">
                              <Eye size={16} />
                            </button>
                            <button onClick={() => abrirModalDocumentos(est)} className="p-1 text-green-600 hover:bg-green-50 rounded" title="Estado Documentos">
                              <FileText size={16} />
                            </button>
                            <button onClick={() => handleEdit(est)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Editar">
                              <Edit size={16} />
                            </button>
                            <button onClick={() => descargarTodosAdjuntos(est)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Descargar adjuntos">
                              <Download size={16} />
                            </button>
                            {tipoFiltro !== 'especial' && (
                              <button onClick={() => descargarFormularioCPAPAA(est)} className="p-1 text-purple-600 hover:bg-purple-50 rounded" title={tipoFiltro === 'preadmision' ? 'Formulario CPA' : 'Formulario PAA'}>
                                <FileText size={16} />
                              </button>
                            )}
                            {est.estado === 'Habilitado' && (
                              <button onClick={() => descargarCertificadoAdmision(est)} className="p-1 text-orange-600 hover:bg-orange-50 rounded" title="Certificado de Admisión">
                                <Download size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 border">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={comentarioEdit[est.id_estudiante] ?? est.comentarios ?? ''}
                              onChange={(e) => handleComentarioChange(est.id_estudiante, e.target.value)}
                              className="w-24 px-1 py-1 text-xs border rounded"
                              placeholder="Comentario"
                            />
                            <button onClick={() => enviarComentario(est)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Enviar por correo">
                              <Send size={14} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <td className="px-2 py-2 text-center border">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => abrirDetalles(est)} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Ver detalles completos">
                            <Eye size={16} />
                          </button>
                          <button onClick={() => descargarTodosAdjuntos(est)} className="p-1 text-gray-600 hover:bg-gray-50 rounded" title="Descargar adjuntos">
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      {/* Modal Añadir/Editar Estudiante */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="ttext-xl font-bold text-blue-900">Añadir Nuevo Estudiante</h3>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div> */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-blue-900">Añadir Nuevo Estudiante
                </h3>
                <p className="text-sm text-gray-600"> {formData.apellidoPaterno},{formData.nombres}</p>
              </div>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* 1. Datos Generales - Solo mostrar en creación, no en edición */}
              {!editingId && (
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Datos Generales</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Modalidad de Postulación*</label>
                      <select name="modalidadPostulacion" value={formData.modalidadPostulacion} onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded-md">
                        <option value="">Seleccionar</option>
                        <option value="CPA">CPA</option>
                        <option value="PAA">PAA</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* 2. Datos Personales */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Datos Personales</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombres*</label>
                    <input type="text" name="nombres" value={formData.nombres} onChange={handleInputChange}
                      placeholder="Ingrese nombres" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Primer Apellido*</label>
                    <input type="text" name="apellidoPaterno" value={formData.apellidoPaterno} onChange={handleInputChange}
                      placeholder="Ingrese primer apellido" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Segundo Apellido</label>
                    <input type="text" name="apellidoMaterno" value={formData.apellidoMaterno} onChange={handleInputChange}
                      placeholder="Ingrese segundo apellido" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-1">Cédula de Identidad*</label>
                    <input type="text" name="cedulaIdentidad" value={formData.cedulaIdentidad} onChange={handleInputChange}
                      placeholder="Ej: 12345678" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Extensión</label>
                    <select name="extension" value={formData.extension} onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md">
                      <option value="">Seleccionar</option>
                      {extensiones.map(e => <option key={e} value={e}>{e}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Complemento</label>
                    <input type="text" name="complemento" value={formData.complemento} onChange={handleInputChange}
                      placeholder="Ej: 1A" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Fecha Nacimiento*</label>
                    <input type="date" name="fechaNacimiento" value={formData.fechaNacimiento} onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Edad*</label>
                    <input type="text" name="edad" value={formData.edad} readOnly
                      className="w-full px-3 py-2 border rounded-md bg-gray-100" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Lugar Nacimiento*</label>
                    <select name="lugarNacimiento" value={formData.lugarNacimiento} onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md">
                      <option value="">Seleccionar</option>
                      {departamentos.map(d => <option key={d} value={d}>{d}</option>)}
                      <option value="Otro">Otro</option>
                    </select>
                  </div>
                  {formData.lugarNacimiento === 'Otro' && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Especificar Lugar*</label>
                      <input type="text" name="lugarNacimientoOtro" value={formData.lugarNacimientoOtro || ''} onChange={handleInputChange}
                        placeholder="Ingrese lugar de nacimiento" className="w-full px-3 py-2 border rounded-md" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Dirección*</label>
                    <input type="text" name="direccion" value={formData.direccion} onChange={handleInputChange}
                      placeholder="Ingrese dirección" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                    <input type="tel" name="numeroTelefono" value={formData.numeroTelefono} onChange={handleInputChange}
                      placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Correo Electrónico*</label>
                    <input type="email" name="correoElectronico" value={formData.correoElectronico} onChange={handleInputChange}
                      placeholder="ejemplo@correo.com" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>

                {/* 3. Datos de Cuenta de Usuario */}
                {!editingId && (
                  <div className="mb-6">
                    <h3 className="text-sm font-bold text-blue-800 uppercase tracking-wider mb-3 bg-blue-50 p-2 rounded">Configuración de Acceso</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-blue-100 rounded-lg bg-white shadow-sm">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Usuario (Opcional)</label>
                        <input type="text" name="username" value={formData.username} onChange={handleInputChange}
                          placeholder="Por defecto: Cédula de Identidad" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Contraseña (Opcional)</label>
                        <input type="password" name="password" value={formData.password} onChange={handleInputChange}
                          placeholder="Por defecto: Cédula de Identidad" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Familiar Cercano */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombres y Apellidos Familiar Cercano*</label>
                    <input type="text" name="familiarNombre" value={formData.familiarNombre} onChange={handleInputChange}
                      placeholder="Nombre completo" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Parentesco*</label>
                    <input type="text" name="familiarParentesco" value={formData.familiarParentesco} onChange={handleInputChange}
                      placeholder="Ej: Padre, Madre, Hermano" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                    <input type="tel" name="familiarTelefono" value={formData.familiarTelefono} onChange={handleInputChange}
                      placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>

                {/* Contacto Emergencia */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Nombres y Apellidos Contacto Emergencia*</label>
                    <input type="text" name="emergenciaNombre" value={formData.emergenciaNombre} onChange={handleInputChange}
                      placeholder="Nombre completo" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Parentesco*</label>
                    <input type="text" name="emergenciaParentesco" value={formData.emergenciaParentesco} onChange={handleInputChange}
                      placeholder="Ej: Padre, Madre, Tío" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Número de Teléfono/Celular*</label>
                    <input type="tel" name="emergenciaTelefono" value={formData.emergenciaTelefono} onChange={handleInputChange}
                      placeholder="Ej: 71234567" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>

                {/* Datos de Salud */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Tipo de Sangre*</label>
                    <input type="text" name="tipoSangre" value={formData.tipoSangre} onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md" placeholder="A+, O-, etc." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Alergias*</label>
                    <input type="text" name="alergias" value={formData.alergias} onChange={handleInputChange}
                      placeholder="Ninguna o especificar" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Enfermedad de Base*</label>
                    <input type="text" name="enfermedadBase" value={formData.enfermedadBase} onChange={handleInputChange}
                      placeholder="Ninguna o especificar" className="w-full px-3 py-2 border rounded-md" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Lesiones u Otros Vinculados a su Salud*</label>
                    <input type="text" name="lesiones" value={formData.lesiones} onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded-md" />
                  </div>
                </div>
              </div>

              {/* 3. Pago de Inscripción */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Pago de Inscripción</h3>
                <p className="text-sm text-gray-600 mb-4">Puede efectuar su pago con cualquiera de los dos siguientes métodos de pago.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <h4 className="font-medium mb-2">Qr</h4>
                    <div className="w-40 h-40 border-2 rounded-lg mx-auto flex items-center justify-center overflow-hidden">
                      {paymentConfig?.qr_imagen_url ? (
                        <img src={paymentConfig.qr_imagen_url} alt="QR" className="w-full h-full object-contain" />
                      ) : (
                        <span className="text-gray-400 text-sm">QR no disponible</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Transferencia Bancaria</h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Banco:</strong> {paymentConfig?.banco_nombre || 'No disponible'}</p>
                      <p><strong>Número de Cuenta:</strong> {paymentConfig?.banco_cuenta || 'No disponible'}</p>
                      <p><strong>Tipo de Cuenta:</strong> Ahorros</p>
                      <p><strong>Nombre Destinatario:</strong> {paymentConfig?.banco_titular || 'EBID'}</p>
                      <p><strong>Glosa:</strong> Pago inscripción</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Documentación */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Documentación</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {documents.map((doc) => (
                    <div key={doc.id}>
                      <label className="block text-sm font-medium mb-1">{doc.name}*</label>
                      <div className="relative">
                        <input
                          type="text"
                          readOnly
                          value={doc.file?.name ? `Nuevo: ${doc.file.name}` : (doc.estado === 'aprobado' ? '✅ Archivo ya cargado' : '')}
                          placeholder="Seleccionar archivo"
                          className={`w-full px-3 py-2 border rounded-md pr-10 text-xs ${doc.estado === 'aprobado' && !doc.file ? 'bg-green-50 border-green-200 text-green-700 font-semibold' : ''}`}
                        />
                        <label className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer">
                          <Paperclip size={18} className="text-gray-400" />
                          <input type="file" onChange={(e) => handleFileChange(doc.id, e)} className="hidden" accept="image/*,.pdf" />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. Declaración Jurada */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Declaración Jurada</h3>
                <div className="bg-gray-50 border rounded-md p-4 text-sm text-gray-700">
                  <p>
                    Yo <strong>{formData.nombres || 'xxxxxxxxxxxxxx'}</strong> hábil por derecho, con <strong>Cédula de Identidad N° {formData.cedulaIdentidad || 'xxxxxxxxx'}</strong> de manera voluntaria acepto los términos establecidos en la Convocatoria y sus procesos de admisión y registro, así mismo declaro en honor a la verdad, que cumplo a cabalidad con los requisitos indispensables exigidos en la convocatoria, siendo que todos los datos proporcionados en el presente formulario son verídicos, por lo que autorizo que la información proporcionada sea verificada y contrastada por la EBID, comprometiéndome a la presentación de la documentación original de acuerdo al cronograma establecido. Asímismo, asumo plena responsabilidad por cualquier situación relacionada con mi salud o accidentes que pudieran ocurrir durante el desarrollo de mis actividades académicas y extracurriculares en la EBID. En este sentido, autorizo/no autorizo a la EBID que, si fuera necesario, contacte a un familiar o contacto de emergencia cuando sea necesario. En caso de no estar de acuerdo con lo estipulado, exonero a la institución de toda responsabilidad respecto de accidentes relacionados a mi persona y de salud dentro de la Institución.
                  </p>
                </div>
              </div>

              {/* Botón Guardar */}
              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
                <button onClick={handleSubmit}
                  className="px-10 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium">
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Documentación */}
      {showDocModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-blue-900">Documentación</h2>
              <button onClick={() => setShowDocModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div>
            <div className="p-6">
              <div className="text-sm text-gray-500 mb-4 bg-blue-50 p-3 rounded">
                Seleccione los nuevos archivos y haga clic en Guardar para actualizar la documentación.
              </div>
              <table className="w-full">
                <thead className="border-b-2">
                  <tr>
                    <th className="px-4 py-3 text-center font-semibold">No</th>
                    <th className="px-4 py-3 text-left font-semibold">Documento</th>
                    <th className="px-4 py-3 text-center font-semibold">Cargar Nuevo</th>
                    <th className="px-4 py-3 text-center font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {documents.map((doc, index) => (
                    <tr key={doc.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-center">{index + 1}</td>
                      <td className="px-4 py-4">
                        <div className="font-medium text-gray-800">{doc.name}</div>
                        {doc.file && <div className="text-xs text-blue-600 font-semibold mt-1">Nuevo: {doc.file.name}</div>}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center">
                          <label className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors border border-gray-300">
                            <Paperclip size={18} className="text-gray-600" />
                            <input type="file" onChange={(e) => handleFileChange(doc.id, e)} className="hidden" accept="image/*,.pdf" />
                          </label>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex justify-center">
                          {doc.file ? (
                            <div className="px-2 py-1 bg-blue-100 text-blue-800 text-[10px] uppercase font-bold rounded-full">
                              Pendiente Guardar
                            </div>
                          ) : doc.estado === 'aprobado' ? (
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center" title="Documento cargado">
                              <span className="text-white font-bold">✓</span>
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center" title="Pendiente">
                              <span className="text-white font-bold">✕</span>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-center mt-6">
                <button
                  onClick={handleSubmit}
                  className="px-10 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium shadow-lg transition-transform active:scale-95"
                >
                  Guardar Cambios
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Ver Detalles Completos */}
      {showDetallesModal && selectedEstudiante && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* <div className="p-6 sticky top-0 bg-white border-b flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-blue-900">Detalles del Estudiante</h2>
              <button onClick={() => setShowDetallesModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
            </div> */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-xl font-bold text-blue-900">
                  Detalles del Estudiante
                </h3>
                {/* <p className="text-sm text-gray-600">{selectedEstudiante.modalidad_postulacion} -{selectedEstudiante.apellido_paterno || ''}, {selectedEstudiante.nombres}</p> */}
              </div>
              <button onClick={() => setShowDetallesModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Datos Personales */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Datos Personales</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="font-medium text-gray-600">Modalidad:</span><p>{selectedEstudiante.modalidad_postulacion || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Nombres:</span><p>{selectedEstudiante.nombres || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Apellidos:</span><p>{`${selectedEstudiante.apellido_paterno || ''} ${selectedEstudiante.apellido_materno || ''}`.trim() || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">CI:</span><p>{selectedEstudiante.cedula_identidad || '-'} {selectedEstudiante.extension || ''}</p></div>
                  <div><span className="font-medium text-gray-600">Complemento:</span><p>{selectedEstudiante.complemento || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Fecha Nacimiento:</span><p>{selectedEstudiante.fecha_nacimiento ? new Date(selectedEstudiante.fecha_nacimiento).toLocaleDateString() : '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Edad:</span><p>{selectedEstudiante.fecha_nacimiento ? Math.floor((new Date() - new Date(selectedEstudiante.fecha_nacimiento)) / (365.25 * 24 * 60 * 60 * 1000)) : '-'} años</p></div>
                  <div><span className="font-medium text-gray-600">Lugar Nacimiento:</span><p>{selectedEstudiante.lugar_nacimiento || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Dirección:</span><p>{selectedEstudiante.direccion || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Teléfono:</span><p>{selectedEstudiante.telefono || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Correo Electrónico:</span><p>{selectedEstudiante.email || '-'}</p></div>
                </div>
              </div>

              {/* Familiar Cercano */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Familiar Cercano</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="font-medium text-gray-600">Nombre:</span><p>{selectedEstudiante.familiar_nombre || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Parentesco:</span><p>{selectedEstudiante.familiar_parentesco || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Teléfono:</span><p>{selectedEstudiante.familiar_telefono || '-'}</p></div>
                </div>
              </div>

              {/* Contacto de Emergencia */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Contacto de Emergencia</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div><span className="font-medium text-gray-600">Nombre:</span><p>{selectedEstudiante.emergencia_nombre || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Parentesco:</span><p>{selectedEstudiante.emergencia_parentesco || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Teléfono:</span><p>{selectedEstudiante.emergencia_telefono || '-'}</p></div>
                </div>
              </div>

              {/* Datos de Salud */}
              <div>
                <h3 className="text-lg font-semibold text-blue-900 pb-2 mb-4">Datos de Salud</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div><span className="font-medium text-gray-600">Tipo de Sangre:</span><p>{selectedEstudiante.tipo_sangre || '-'}</p></div>
                  <div><span className="font-medium text-gray-600">Alergias:</span><p>{selectedEstudiante.alergias || 'Ninguna'}</p></div>
                  <div><span className="font-medium text-gray-600">Enfermedad de Base:</span><p>{selectedEstudiante.enfermedad_base || 'Ninguna'}</p></div>
                  <div><span className="font-medium text-gray-600">Lesiones u Otros:</span><p>{selectedEstudiante.lesiones || 'Ninguna'}</p></div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-100">
                <button onClick={() => setShowDetallesModal(false)}
                  className="px-6 py-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 font-medium text-sm transition shadow-sm">
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}